import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Building2, X as XIcon } from "lucide-react";

export default function MigrateStudiosButton({ onDone }) {
  const [status, setStatus] = useState(null); // {pending_scenes, ...}
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/api/admin/migrate-studios-status").then(r => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  if (!status || status.pending_scenes === 0) {
    return null;
  }

  const loadPlan = async () => {
    setBusy(true);
    try {
      const r = await api.post("/api/admin/migrate-studios-to-catalog", {});
      setPlan(r.data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load migration plan");
    } finally { setBusy(false); }
  };

  const confirmRun = async () => {
    if (!window.confirm(`Migrate ${plan.to_create} studio scene(s) into the catalog as videos?\n\nLegacy /studios data will be kept untouched. This is reversible by deleting the new videos.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/api/admin/migrate-studios-to-catalog", { confirm: true });
      toast.success(`Migrated ${r.data.to_create} scene${r.data.to_create === 1 ? "" : "s"} → catalog`);
      setOpen(false);
      const sr = await api.get("/api/admin/migrate-studios-status");
      setStatus(sr.data);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={loadPlan} disabled={busy} data-testid="migrate-studios-button"
        className="btn-ghost inline-flex items-center gap-2"
        title={`${status.pending_scenes} legacy studio scene(s) to migrate`}>
        <Building2 size={14} /> Migrate studios ({status.pending_scenes})
      </button>
      {open && plan && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="migrate-modal">
          <div className="bg-zinc-950 border border-zinc-800 max-w-2xl w-full max-h-[88vh] overflow-y-auto rounded-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-zinc-900 flex items-center justify-between">
              <div>
                <div className="overline" style={{ color: "#a855f7" }}>STUDIOS → CATALOG · DRY RUN</div>
                <div className="text-[13px] mt-1" style={{ color: "var(--gio-muted)" }}>
                  Will create <strong className="text-white">{plan.to_create}</strong> new video(s) ·
                  {" "}<strong className="text-white">{plan.already_migrated}</strong> already migrated ·
                  {" "}<strong className="text-white">{plan.without_linked_crm}</strong> need manual CRM link
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="migrate-close"><XIcon size={18} /></button>
            </div>
            <div className="p-5 space-y-2 max-h-[55vh] overflow-y-auto">
              {plan.plan.map((row, i) => (
                <div key={i} className="flex items-start gap-3 p-2.5 rounded border text-[12px]"
                  data-testid={`migrate-plan-row-${i}`}
                  style={{
                    borderColor: row.action === "skip_already_migrated" ? "var(--gio-border)" : "rgba(168,85,247,0.4)",
                    background: row.action === "skip_already_migrated" ? "var(--gio-surface)" : "rgba(168,85,247,0.05)",
                  }}>
                  <div className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
                    style={{
                      background: row.action === "create_video" ? "rgba(168,85,247,0.2)" : "rgba(255,255,255,0.05)",
                      color: row.action === "create_video" ? "#a855f7" : "var(--gio-muted-3)",
                    }}>
                    {row.action === "create_video" ? "CREATE" : "SKIP"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white">
                      {row.studio_name} <span style={{ color: "var(--gio-muted-3)" }}>·</span> {row.scene_name}
                    </div>
                    {row.action === "create_video" && (
                      <div className="text-[10px] mt-0.5" style={{ color: "var(--gio-muted-2)" }}>
                        Scene #{row.scene_number}
                        {row.fee > 0 && <> · {row.fee.toLocaleString()} {row.fee_currency}</>}
                        {row.has_mux && <> · Mux ✓</>}
                        {!row.linked_crm && <span className="text-amber-400"> · no CRM link</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {plan.plan.length === 0 && (
                <div className="text-center py-8 text-[12px]" style={{ color: "var(--gio-muted-3)" }}>
                  Nothing to migrate.
                </div>
              )}
            </div>
            <div className="p-5 border-t border-zinc-900 flex gap-3">
              <button onClick={confirmRun} disabled={busy || plan.to_create === 0}
                data-testid="migrate-confirm"
                className="btn-primary flex-1 disabled:opacity-40">
                {busy ? "Running…" : `Migrate ${plan.to_create} scene${plan.to_create === 1 ? "" : "s"}`}
              </button>
              <button onClick={() => setOpen(false)} data-testid="migrate-cancel" className="btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
